<script setup lang="ts">
import {ref} from "vue";
import {laboratoryApi} from "@/container";
import {ReferentLaboratory} from "@/core/interfaces/TeamPlanning";
import BaseProductSearchBar from "@/product/components/BaseProductSearchBar.vue";
import {FlaskConical, X} from "@lucide/vue";

const {modelValue} = defineProps<{
    /** The selected laboratories. */
    modelValue: ReferentLaboratory[];
}>();

const emits = defineEmits<{ (event: 'update:modelValue', laboratories: ReferentLaboratory[]): void }>();

/** Remounts the search bar after each pick, clearing its input. */
const searchBarKey = ref<number>(0);

const searchLaboratories = (query: string): Promise<ReferentLaboratory[]> =>
    laboratoryApi().searchDirectory(query);

const add = (laboratory: ReferentLaboratory) => {
    if (!modelValue.some(item => item.id === laboratory.id)) {
        emits('update:modelValue', [...modelValue, laboratory]);
    }

    searchBarKey.value++;
};

const remove = (laboratory: ReferentLaboratory) => {
    emits('update:modelValue', modelValue.filter(item => item.id !== laboratory.id));
};
</script>

<template>
  <div class="flex flex-wrap items-center gap-1.5">
    <span
      v-for="laboratory in modelValue"
      :key="laboratory.id"
      class="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800"
    >
      <FlaskConical class="size-3" />
      {{ laboratory.name }}
      <button
        :title="`Retirer ${laboratory.name}`"
        class="opacity-60 hover:opacity-100 transition-opacity"
        @click="remove(laboratory)"
      >
        <X class="size-3.5" />
      </button>
    </span>
    <BaseProductSearchBar
      :key="searchBarKey"
      class="min-w-56"
      input-class="h-8 text-xs"
      placeholder="Rechercher un laboratoire…"
      :search-fn="searchLaboratories"
      @select="add"
    />
  </div>
</template>
